import { useEffect, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createTournament, getTournament, updateTournament } from '@/lib/api';
import { showErrorToast } from '@/lib/toast';
import type {
  TimeControl,
  TiebreakCriterion,
  PlayerListVisibility,
  TournamentSystem,
  CreateTournamentRequest,
  UpdateTournamentRequest,
  RequiredProfileField,
  TournamentTemplateValues,
  KoFinishTop,
} from '@swiss-magic/shared-types';
import {
  getGameConfig,
  allowedRequiredProfileFields,
} from '@swiss-magic/shared-types';

type Input = {
  id: string | undefined;
  isForceEdit: boolean;
  /** Story 093 — co-directors view settings read-only. */
  isCoDirector?: boolean;
  navigate: NavigateFunction;
};

export function useTournamentFormController({
  id,
  isForceEdit,
  isCoDirector = false,
  navigate,
}: Input) {
  const { t } = useTranslation('director');
  const isEdit = Boolean(id);

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  // Story 096 — FIDE rating-grade tournament metadata (chess).
  const [endDate, setEndDate] = useState('');
  const [federation, setFederation] = useState('');
  const [chiefArbiterName, setChiefArbiterName] = useState('');
  const [chiefArbiterFideId, setChiefArbiterFideId] = useState('');
  const [chiefArbiterTitle, setChiefArbiterTitle] = useState('');
  const [location, setLocation] = useState('');
  const [rounds, setRounds] = useState('');
  const [timeControl, setTimeControl] = useState<TimeControl | ''>('');
  const [description, setDescription] = useState('');
  const [maxPlayers, setMaxPlayers] = useState('');
  const [entryFee, setEntryFee] = useState('');
  const [tiebreakCriteria, setTiebreakCriteria] = useState<TiebreakCriterion[]>(
    getGameConfig('chess').tiebreakCriteria.slice(0, 5) as TiebreakCriterion[],
  );
  const [playerListVisibility, setPlayerListVisibility] = useState<PlayerListVisibility>('list');
  const [system, setSystem] = useState<TournamentSystem>('swiss');
  const [game, setGame] = useState('chess');
  const [gameName, setGameName] = useState('Chess');
  const [matchLength, setMatchLength] = useState('');
  const [requiredProfileFields, setRequiredProfileFields] = useState<
    RequiredProfileField[]
  >([]);
  // Stories 057 + 058 — defaults preserve current behaviour (public,
  // online-registration enabled). Both are simple booleans driven by
  // checkboxes on the form.
  const [isPrivate, setIsPrivate] = useState(false);
  const [onlineRegistration, setOnlineRegistration] = useState(true);
  // Story 100 — how a self-registered player's spot gets secured.
  const [spotConfirmationMode, setSpotConfirmationMode] = useState<'manual' | 'auto'>('manual');
  // Story 105 — 3ko/5ko qualifier finish: end when the field is reduced to
  // this many still-in players (powers of two). 1 = today's play-to-the-end.
  const [koFinishTop, setKoFinishTop] = useState<KoFinishTop>(1);
  // Story 010b — per-tournament confirmation mode. Default 'organizer_
  // confirmation' is FIDE-safe; player_confirmation lets opponents
  // finalize a player-self-reported result. Locked once the
  // tournament is in_progress (round 1 has been generated).
  const [resultConfirmationMode, setResultConfirmationMode] = useState<
    'organizer_confirmation' | 'player_confirmation'
  >('organizer_confirmation');
  // Story 084 — optional external URL + label + venue map URL.
  const [externalUrl, setExternalUrl] = useState('');
  const [externalUrlLabel, setExternalUrlLabel] = useState('');
  const [venueMapUrl, setVenueMapUrl] = useState('');
  const [tournamentStatus, setTournamentStatus] = useState('draft');
  const [nameIsLocked, setNameIsLocked] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const gameConfig = getGameConfig(game);
  const isKoSystem = system === '3ko' || system === '5ko';
  const availableTiebreaks = (isKoSystem
    ? gameConfig.tiebreakCriteria
    : gameConfig.tiebreakCriteria.filter((c) => !gameConfig.koOnlyTiebreaks.includes(c))
  ) as TiebreakCriterion[];

  useEffect(() => {
    if (id) {
      getTournament(id)
        .then((t) => {
          setName(t.name);
          setStartDate(t.startDate || '');
          setEndDate(t.endDate ?? '');
          setFederation(t.federation ?? '');
          setChiefArbiterName(t.chiefArbiterName ?? '');
          setChiefArbiterFideId(
            t.chiefArbiterFideId != null ? String(t.chiefArbiterFideId) : '',
          );
          setChiefArbiterTitle(t.chiefArbiterTitle ?? '');
          setLocation(t.location || '');
          setRounds(t.rounds ? String(t.rounds) : '');
          setTimeControl(t.timeControl || '');
          setDescription(t.description || '');
          setMaxPlayers(t.maxPlayers ? String(t.maxPlayers) : '');
          setEntryFee(t.entryFee || '');
          setTiebreakCriteria(t.tiebreakCriteria);
          setPlayerListVisibility(t.playerListVisibility);
          setSystem(t.system || 'swiss');
          setGame(t.game || 'chess');
          setGameName(t.gameName || 'Chess');
          setMatchLength(t.matchLength ? String(t.matchLength) : '');
          setRequiredProfileFields(t.requiredProfileFields ?? []);
          setIsPrivate(t.isPrivate ?? false);
          setOnlineRegistration(t.onlineRegistration ?? true);
          setSpotConfirmationMode(t.spotConfirmationMode ?? 'manual');
          setKoFinishTop(t.koFinishTop ?? 1);
          setResultConfirmationMode(
            t.resultConfirmationMode ?? 'organizer_confirmation',
          );
          setExternalUrl(t.externalUrl ?? '');
          setExternalUrlLabel(t.externalUrlLabel ?? '');
          setVenueMapUrl(t.venueMapUrl ?? '');
          setTournamentStatus(t.status);
          setNameIsLocked(t.hasBeenPublished);
          setLoading(false);
        })
        .catch(() => {
          navigate('/dashboard');
        });
    }
    // Deliberately NOT depending on isForceEdit: God Mode is a live hub
    // header toggle now (story 083), and refetching on its flip would
    // overwrite in-progress edits. readOnly is derived from it in render
    // instead, so toggling only locks/unlocks the fieldset.
  }, [id, navigate]);

  // Non-draft tournaments are read-only unless God Mode (force edit) is on.
  // Derived in render so the live hub toggle flips it without a refetch.
  // Co-directors are always read-only (settings are owner-only).
  const readOnly =
    isCoDirector || (isEdit && tournamentStatus !== 'draft' && !isForceEdit);

  function handleGameChange(v: string) {
    setGame(v);
    const config = getGameConfig(v);
    setGameName(config.name);
    if (config.timeControls.length === 0) setTimeControl('');
    if (!config.tournamentSystems.includes(system)) {
      setSystem(config.tournamentSystems[0] as TournamentSystem);
    }
    const isKo = system === '3ko' || system === '5ko';
    const criteria = isKo
      ? config.tiebreakCriteria.slice(0, 5)
      : config.tiebreakCriteria
          .filter((c) => !config.koOnlyTiebreaks.includes(c))
          .slice(0, 5);
    setTiebreakCriteria(criteria as TiebreakCriterion[]);
    // Drop required-profile fields that don't apply to the new game so the
    // save doesn't get rejected by the API allowed-list check.
    const allowed = new Set(allowedRequiredProfileFields(v));
    setRequiredProfileFields((prev) => prev.filter((f) => allowed.has(f)));
  }

  function toggleRequiredProfileField(field: RequiredProfileField) {
    setRequiredProfileFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field],
    );
  }

  function handleSystemChange(v: TournamentSystem) {
    setSystem(v);
    const config = getGameConfig(game);
    const isKo = v === '3ko' || v === '5ko';
    if (isKo && config.koOnlyTiebreaks.length > 0) {
      setTiebreakCriteria((prev) => {
        const missing = config.koOnlyTiebreaks.filter(
          (c) => !prev.includes(c as TiebreakCriterion),
        );
        return [...missing, ...prev] as TiebreakCriterion[];
      });
    } else if (!isKo && config.koOnlyTiebreaks.length > 0) {
      setTiebreakCriteria(
        (prev) =>
          prev.filter((c) => !config.koOnlyTiebreaks.includes(c)) as TiebreakCriterion[],
      );
    }
  }

  function addCriterion(criterion: TiebreakCriterion) {
    setTiebreakCriteria((prev) => [...prev, criterion]);
  }

  function removeCriterion(criterion: TiebreakCriterion) {
    setTiebreakCriteria((prev) => prev.filter((c) => c !== criterion));
  }

  function moveCriterion(index: number, direction: -1 | 1) {
    setTiebreakCriteria((prev) => {
      const next = [...prev];
      const target = index + direction;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  // Story 078 — capture the current form snapshot for save-as-
  // template. Deliberately excludes the four tournament-specific
  // identifying fields (name / description / startDate / location)
  // — those are always entered fresh per tournament.
  function captureTemplateValues(): TournamentTemplateValues {
    return {
      game,
      gameName: game === 'other' ? gameName : null,
      system,
      timeControl: timeControl || null,
      matchLength: matchLength ? Number(matchLength) : null,
      ...(rounds ? { rounds: Number(rounds) } : {}),
      tiebreakCriteria,
      isPrivate,
      onlineRegistration,
      spotConfirmationMode,
      koFinishTop,
      maxPlayers: maxPlayers ? Number(maxPlayers) : null,
      entryFee: entryFee.trim() || null,
      requiredProfileFields,
      playerListVisibility,
      resultConfirmationMode,
    };
  }

  // Story 078 — apply a template snapshot to the form. Identifying
  // fields stay empty so the director enters them. Game is applied
  // first via handleGameChange so tiebreak / required-profile
  // adjustments fire correctly, then the template's specific
  // tiebreaks / required-profile fields override the defaults.
  function applyTemplateValues(v: TournamentTemplateValues) {
    if (typeof v.game === 'string') {
      handleGameChange(v.game);
      if (v.game === 'other' && typeof v.gameName === 'string') {
        setGameName(v.gameName);
      }
    }
    if (typeof v.system === 'string') {
      handleSystemChange(v.system as TournamentSystem);
    }
    if (v.timeControl !== undefined) {
      setTimeControl((v.timeControl ?? '') as TimeControl | '');
    }
    if (v.matchLength != null) {
      setMatchLength(String(v.matchLength));
    } else {
      setMatchLength('');
    }
    if (v.rounds != null) {
      setRounds(String(v.rounds));
    } else {
      setRounds('');
    }
    if (Array.isArray(v.tiebreakCriteria)) {
      setTiebreakCriteria(v.tiebreakCriteria as TiebreakCriterion[]);
    }
    if (typeof v.isPrivate === 'boolean') setIsPrivate(v.isPrivate);
    if (typeof v.onlineRegistration === 'boolean')
      setOnlineRegistration(v.onlineRegistration);
    if (v.spotConfirmationMode === 'manual' || v.spotConfirmationMode === 'auto')
      setSpotConfirmationMode(v.spotConfirmationMode);
    if (typeof v.koFinishTop === 'number')
      setKoFinishTop(v.koFinishTop as KoFinishTop);
    if (v.maxPlayers != null) setMaxPlayers(String(v.maxPlayers));
    else setMaxPlayers('');
    setEntryFee(v.entryFee ?? '');
    if (Array.isArray(v.requiredProfileFields)) {
      setRequiredProfileFields(
        v.requiredProfileFields as RequiredProfileField[],
      );
    }
    if (v.playerListVisibility) {
      setPlayerListVisibility(v.playerListVisibility);
    }
    if (v.resultConfirmationMode) {
      setResultConfirmationMode(v.resultConfirmationMode);
    }
  }

  // Story 084 — HTTPS-only check for the two URL fields. Mirrors the
  // backend's @IsUrl rule so the UI can show an inline error instead
  // of bubbling a 400. Empty inputs are fine (the fields are optional).
  function isAcceptableUrl(raw: string): boolean {
    if (!raw.trim()) return true;
    try {
      const u = new URL(raw.trim());
      return u.protocol === 'https:';
    } catch {
      return false;
    }
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = t('tournament_form.errors.name_required');
    if (rounds && Number(rounds) <= 0)
      errs.rounds = t('tournament_form.errors.rounds_positive');
    if (
      (system === 'round_robin' || system === 'double_round_robin') &&
      maxPlayers &&
      Number(maxPlayers) > 20
    ) {
      errs.maxPlayers = t('tournament_form.errors.round_robin_max_players');
    }
    if (!isAcceptableUrl(externalUrl)) {
      errs.externalUrl = t('tournament_form.errors.https_required');
    }
    if (!isAcceptableUrl(venueMapUrl)) {
      errs.venueMapUrl = t('tournament_form.errors.https_required');
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    try {
      const data: CreateTournamentRequest & UpdateTournamentRequest = {
        name: name.trim(),
        ...(startDate ? { startDate } : {}),
        ...(location.trim() ? { location: location.trim() } : {}),
        ...(rounds ? { rounds: Number(rounds) } : {}),
        ...(timeControl ? { timeControl: timeControl as TimeControl } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(maxPlayers ? { maxPlayers: Number(maxPlayers) } : {}),
        ...(entryFee.trim() ? { entryFee: entryFee.trim() } : {}),
        tiebreakCriteria,
        playerListVisibility,
        system,
        game,
        gameName: game === 'other' ? gameName : getGameConfig(game).name,
        ...(matchLength ? { matchLength: Number(matchLength) } : {}),
        requiredProfileFields,
        isPrivate,
        onlineRegistration,
        spotConfirmationMode,
        // Story 105 — only KO systems carry a meaningful finish-top; other
        // systems always send the default 1 (play to the end).
        koFinishTop:
          system === '3ko' || system === '5ko' ? koFinishTop : 1,
        resultConfirmationMode,
        // Story 096 — FIDE rating-grade metadata (chess only). On edit send
        // null to clear; on create include only when present.
        ...(game === 'chess'
          ? isEdit
            ? {
                endDate: endDate || null,
                federation: federation.trim() || null,
                chiefArbiterName: chiefArbiterName.trim() || null,
                chiefArbiterFideId: chiefArbiterFideId
                  ? Number(chiefArbiterFideId)
                  : null,
                chiefArbiterTitle: chiefArbiterTitle.trim() || null,
              }
            : {
                ...(endDate ? { endDate } : {}),
                ...(federation.trim()
                  ? { federation: federation.trim() }
                  : {}),
                ...(chiefArbiterName.trim()
                  ? { chiefArbiterName: chiefArbiterName.trim() }
                  : {}),
                ...(chiefArbiterFideId
                  ? { chiefArbiterFideId: Number(chiefArbiterFideId) }
                  : {}),
                ...(chiefArbiterTitle.trim()
                  ? { chiefArbiterTitle: chiefArbiterTitle.trim() }
                  : {}),
              }
          : {}),
        // Story 084 — on edit, send null to clear; on create just omit.
        ...(isEdit
          ? {
              externalUrl: externalUrl.trim() || null,
              externalUrlLabel: externalUrl.trim()
                ? externalUrlLabel.trim() || null
                : null,
              venueMapUrl: venueMapUrl.trim() || null,
            }
          : {
              ...(externalUrl.trim() ? { externalUrl: externalUrl.trim() } : {}),
              ...(externalUrl.trim() && externalUrlLabel.trim()
                ? { externalUrlLabel: externalUrlLabel.trim() }
                : {}),
              ...(venueMapUrl.trim() ? { venueMapUrl: venueMapUrl.trim() } : {}),
            }),
      };

      if (isEdit && id) {
        await updateTournament(id, data, isForceEdit);
        navigate(`/tournaments/${id}`);
      } else {
        const created = await createTournament(data);
        navigate(`/tournaments/${created.id}`);
      }
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : t('tournament_form.errors.save_failed');
      showErrorToast(msg);
      setSubmitting(false);
    }
  }

  return {
    // meta
    isEdit,
    loading,
    submitting,
    readOnly,
    nameIsLocked,
    tournamentStatus,
    errors,
    // fields
    name,
    setName,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    federation,
    setFederation,
    chiefArbiterName,
    setChiefArbiterName,
    chiefArbiterFideId,
    setChiefArbiterFideId,
    chiefArbiterTitle,
    setChiefArbiterTitle,
    location,
    setLocation,
    rounds,
    setRounds,
    timeControl,
    setTimeControl,
    description,
    setDescription,
    maxPlayers,
    setMaxPlayers,
    entryFee,
    setEntryFee,
    tiebreakCriteria,
    playerListVisibility,
    setPlayerListVisibility,
    system,
    game,
    gameName,
    setGameName,
    matchLength,
    setMatchLength,
    requiredProfileFields,
    toggleRequiredProfileField,
    isPrivate,
    setIsPrivate,
    onlineRegistration,
    setOnlineRegistration,
    spotConfirmationMode,
    setSpotConfirmationMode,
    koFinishTop,
    setKoFinishTop,
    resultConfirmationMode,
    setResultConfirmationMode,
    // Story 084 — optional external URL + label + venue map URL.
    externalUrl,
    setExternalUrl,
    externalUrlLabel,
    setExternalUrlLabel,
    venueMapUrl,
    setVenueMapUrl,
    // derived
    gameConfig,
    availableTiebreaks,
    availableRequiredProfileFields: allowedRequiredProfileFields(game),
    // handlers
    handleGameChange,
    handleSystemChange,
    addCriterion,
    removeCriterion,
    moveCriterion,
    handleSubmit,
    // template integration (story 078)
    captureTemplateValues,
    applyTemplateValues,
  };
}

export type TournamentFormController = ReturnType<typeof useTournamentFormController>;
